import { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import cn from 'clsx';

import { UpdateUserInput } from '@sdk/API';
import { getUserByEmail, updateUserByEmail } from '@sdk/managers/UsersManager';
import { subYears, isBefore } from 'date-fns';
import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';
import { Colors } from '@components/elements/Colors';
import { DatePicker } from '@components/elements/DatePicker';

import './EditAccountPage.css';

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { UserSalaryRange } from '../../../../../../sdk/src/API';

const FORM_SCHEMA = z
  .object({
    username: z.any(), // Not editable here
    email: z.any(), // Not editable here
    firstName: z
      .string()
      .min(1, 'Required')
      .regex(/^[A-Za-z ]*$/, 'Invalid format'),
    lastName: z
      .string()
      .min(1, 'Required')
      .regex(/^[A-Za-z ]*$/, 'Invalid format'),
    gender: z
      .union([
        z.literal('Default'),
        z.literal('Male'),
        z.literal('Female'),
        z.literal('Other'),
      ])
      .nullable(),
    yearOfBirth: z.date(),
    education: z.string().nullable(),
    phone: z.string().nullable(),
    salaryRange: z.string().nullable(),
    cryptowallet:z.string().nullable(),
    postalCode: z
      .string()
      .regex(/^[0-9]*$/, 'Postal Code can only contain numbers.')
      .nullable(),
    organisation: z.string().nullable(),
    department: z.string().nullable(),
    jobTitle: z.string().nullable(),
  })
  .refine((data) => isBefore(data.yearOfBirth, subYears(new Date(), 16)), {
    message: 'You must be at least 16 years old.',
    path: ['yearOfBirth'], // set path of error
  });

type FormInput = z.infer<typeof FORM_SCHEMA>;

type User = UpdateUserInput;

const dropdownCn =
  'input-text rounded-md  bg-white py-3 px-2 w-full font-display text-sm outline-none placeholder-opacity-80 focus:placeholder-opacity-40 text-gray-darkest text-opacity-100 focus:text-opacity-80 placeholder-purple';

const SALARY_RANGE = {
  '1': { value: '_1', label: 'Below $11,999' },
  '2': { value: '_2', label: '$12,000 - $29,999' },
  '3': { value: '_3', label: '$30,000 - $49,999' },
  '4': { value: '_4', label: '$50,000 - $79,999' },
  '5': { value: '_5', label: '$80,000 - $119,999' },
  '6': { value: '_6', label: '$120,000 and above' },
};

const EditAccountPage: FC = () => {
  const history = useHistory<any>();
  const { user: authUser, dbUser, refetch } = useAuth();
  const [user, setUser] = useState<User>();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    control,
    reset,
    getValues,
  } = useForm<FormInput>({
    resolver: zodResolver(FORM_SCHEMA),
    shouldUnregister: false,
    defaultValues: {
      email: dbUser?.email,
      username: dbUser?.username,
      firstName: dbUser?.firstName,
      lastName: dbUser?.lastName,
      cryptowallet:dbUser?.cryptowallet,
      phone: dbUser?.phone,
      gender: dbUser?.gender,
      postalCode: dbUser?.firstName,
      yearOfBirth: dbUser?.yearOfBirth
        ? new Date(parseInt(dbUser.yearOfBirth), 1, 1)
        : undefined,
      organisation: dbUser?.organisation,
    },
  });

  const init = useCallback(async () => {
    let res;
    try {
      res = await getUserByEmail(
        [
          {
            items: [
              'id',
              'username',
              'firstName',
              'lastName',
              'gender',
              'email',
              'phone',
              'yearOfBirth',
              'education',
              'cryptowallet',
              'postalCode',
              'salaryRange',
              'image { key bucket region }',
              'organisation { name department jobTitle }',
            ],
          },
        ],
        authUser.attributes.email
      );
     
    } catch (err: any) {
      console.error('fetching user details', err);
    }

    try {
      const {
        data: {
          result: { items: users },
        },
      } = res;

      // Select first user that isnt null
      let userDetails = users.filter((u) => !!u);
      if (!userDetails || userDetails.length < 1)
        throw new Error('Failed to retrieve user details.');
      userDetails = userDetails[0];
      console.log(userDetails)
      setUser(userDetails);
      refetch();
    } catch (err: any) {
      console.error('parse user details', err);
    }
  }, [setUser, refetch, authUser]);

  useEffect(() => {
    if (authUser) init();
  }, [authUser, init, setUser]);

  useEffect(() => {
    if (!user) return;

    const payload: Partial<FormInput> = {
      education: user.education ? user.education : 'Default',
      salaryRange: user.salaryRange ? user.salaryRange : 'Default',
    };

    if (user.username) payload.username = user.username;
    if (user.email) payload.email = user.email;
    if (user.firstName) payload.firstName = user.firstName;
    if (user.lastName) payload.lastName = user.lastName;
    if (user.phone) payload.phone = user.phone;
    if (user.gender)
      payload.gender = user.gender as 'Male' | 'Female' | 'Other';
    if (user.postalCode) payload.postalCode = user.postalCode;
    if (user.cryptowallet) payload.cryptowallet = user.cryptowallet;
    if (user.salaryRange) payload.salaryRange = user.salaryRange;
    if (user.organisation?.name) payload.organisation = user.organisation.name;
    if (user.organisation?.department)
      payload.department = user.organisation.department;
    if (user.organisation?.jobTitle)
      payload.jobTitle = user.organisation.jobTitle;
    if (user.yearOfBirth)
      payload.yearOfBirth = new Date(user.yearOfBirth, 1, 1);

    console.log('init', payload);

    reset(payload);
  }, [reset, user]);

  const onSubmit = useCallback(
    async (data: FormInput) => {
      console.log('onSubmit', { user, data });
      if (dbUser === undefined || !dbUser.id) return;

      try {
        const payload: UpdateUserInput = {
          id: dbUser.id,
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender,
          phone: data.phone,
          postalCode: data.postalCode,
          cryptowallet:data.cryptowallet,
          yearOfBirth: data.yearOfBirth.getFullYear(),
          organisation: {
            name: data.organisation,
            department: data.department,
            jobTitle: data.jobTitle,
          },
        };

        if (data.salaryRange !== 'Default') {
          payload.salaryRange = data.salaryRange as UserSalaryRange;
        }
        if (data.education !== 'Default') {
          payload.education = data.education?.toString();
        }

        // Dont add if undefined, updating to graphql gets error if undefined
        Object.keys(payload).forEach((key) => {
          const v = payload[key];
          if (!v || v === '') delete payload[key];
        });
        if (payload.organisation) {
          Object.keys(payload.organisation).forEach((key) => {
            const v = payload.organisation?.[key];
            if (!v || v === '') delete payload.organisation?.[key];
          });
        }

        // Only add if organisation has values
        if (
          !payload.organisation ||
          Object.keys(payload.organisation).length === 0
        )
          delete payload.organisation;

        try {
          await updateUserByEmail(['id'], authUser.attributes.email, payload);

          await refetch();
          await history.goBack();
        } catch (err: any) {
          console.error(err);
          toast.error(err.message);
        }
      } catch (err: any) {
        console.error(err);
        toast.error(err.message);
      }
    },
    [user, dbUser, authUser.attributes.email, refetch, history]
  );

  const labelCn = 'font-bold text-lg mb-1';
  const captionCn = 'text-red text-right';

  const leftContent = (
    <>
      <div className="p-4 space-y-3 text-black bg-yellow">
        <div>
          <InputText
            placeholder="username"
            disabled
            {...register('username')}
          />
        </div>
        <div>
          <InputText placeholder="email" disabled {...register('email')} />
        </div>

        <div className="py-2">
          <Link to="/account/edit/password">
            <ButtonMighty
              disabled={isSubmitting}
              loading={isSubmitting}
              color="#8052FF"
              text="Change password"
              borderColor="#f97075"
              margin="0"
              textColor="#fff"
            />
          </Link>
        </div>
      </div>
    </>
  );

  const content = (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-4 space-y-3 text-black bg-yellow"
      >
        <div>
          <h3 className={labelCn}>First & Last Name</h3>
          <div className="pb-3 space-y-3">
            <div>
              <InputText
                type="text"
                placeholder="First Name"
                shadowColor={Colors.green}
                borderColor={Colors.red}
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className={captionCn}>*{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <InputText
                type="text"
                placeholder="Last Name"
                shadowColor={Colors.green}
                borderColor={Colors.red}
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className={captionCn}>*{errors.lastName.message}</p>
              )}
            </div>
            <div className="account-edit-field-gender">
              <select
                className={cn(dropdownCn, 'border-red')}
                style={{
                  boxShadow: `4px 4px 0px 0px ${Colors.green}`,
                }}
                defaultValue="Default"
                {...register('gender')}
              >
                <option value="Default" disabled>
                  Select your gender
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Prefer not to say</option>
              </select>
              {errors.gender && (
                <p className={captionCn}>*{errors.gender.message}</p>
              )}
            </div>
            <div className="account-edit-field-datepicker">
              <Controller
                name="yearOfBirth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    end={subYears(new Date(), 16)}
                    start={subYears(new Date(), 100)}
                    interval="year"
                    title="Select your year of birth"
                    placeholder="Year of Birth"
                    shadowColor={Colors.green}
                    borderColor={Colors.red}
                    name="yearOfBirth"
                    onChange={(value) => setValue('yearOfBirth', value)}
                    value={getValues('yearOfBirth')}
                  />
                )}
              />

              {errors.yearOfBirth && (
                <p className={captionCn}>{errors.yearOfBirth}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className={labelCn}>Phone Number</h3>
          <InputText
            type="tel"
            placeholder="Phone Number"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('phone')}
          />
          {
            <p className={cn(captionCn, { 'font-bold': !!errors.phone })}>
              {errors.phone ? `*${errors.phone.message}` : 'Optional'}
            </p>
          }
        </div>

        <div>
          <h3 className={labelCn}>Education</h3>
          <div>
            <select
              className={cn(dropdownCn, 'border-blue')}
              style={{
                boxShadow: `4px 4px 0px 0px ${Colors.red}`,
              }}
              defaultValue="Default"
              {...register('education')}
            >
              <option value="Default" disabled>
                Select your Education level
              </option>
              {[
                'O Levels',
                'A Levels',
                'Diploma',
                'Degree',
                'Honours',
                'Masters',
                'Doctorate / PhD',
              ].map((value) => (
                <option key={`education-option-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <p className={cn(captionCn, { 'font-bold': !!errors.education })}>
              {errors.education ? `*${errors.education.message}` : 'Optional'}
            </p>
          </div>
        </div>

        <div>
          <h3 className={labelCn}>Postal Code</h3>
          <InputText
            type="text"
            placeholder="Postal Code"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('postalCode')}
          />
          {
            <p className={cn(captionCn, { 'font-bold': !!errors.postalCode })}>
              {errors.postalCode ? `*${errors.postalCode.message}` : 'Optional'}
            </p>
          }
        </div>
        <div>
          <h3 className={labelCn}>Crypto Wallet (Eg Metamask, Coinbase Wallet...)</h3>
          <InputText
            type="text"
            placeholder="Crypto Wallet Address"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('cryptowallet')}
          />
          {
            <p className={cn(captionCn, { 'font-bold': !!errors.cryptowallet })}>
              {errors.cryptowallet ? `*${errors.cryptowallet.message}` : 'Optional'}
            </p>
          }
        </div>
        <div>
          <h3 className={labelCn}>Annual Salary Range</h3>
          <div>
            <select
              className={cn(dropdownCn, 'border-blue')}
              style={{
                boxShadow: `4px 4px 0px 0px ${Colors.red}`,
              }}
              defaultValue="Default"
              {...register('salaryRange')}
            >
              <option value="Default" disabled>
                Select your Salary range
              </option>
              {Object.keys(SALARY_RANGE).map((key) => {
                const salary = SALARY_RANGE[key];
                return (
                  <option
                    key={`salary-option-${salary.value}`}
                    value={salary.value}
                  >
                    {salary.label}
                  </option>
                );
              })}
            </select>
            <p className={cn(captionCn, { 'font-bold': !!errors.salaryRange })}>
              {errors.salaryRange
                ? `*${errors.salaryRange.message}`
                : 'Optional'}
            </p>
          </div>
        </div>

        <div>
          <h3 className={labelCn}>Organisation</h3>
          <InputText
            type="text"
            placeholder="Organisation Name"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('organisation')}
          />
          {
            <p
              className={cn(captionCn, {
                'font-bold': !!errors.organisation,
              })}
            >
              {errors.organisation
                ? `*${errors.organisation.message}`
                : 'Optional'}
            </p>
          }
        </div>

        <div>
          <h3 className={labelCn}>Department</h3>
          <InputText
            type="text"
            placeholder="Department Name"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('department')}
          />
          {
            <p className={cn(captionCn, { 'font-bold': !!errors.department })}>
              {errors.department ? `*${errors.department.message}` : 'Optional'}
            </p>
          }
        </div>

        <div>
          <h3 className={labelCn}>Job Title</h3>
          <InputText
            type="text"
            placeholder="Job Title"
            shadowColor={Colors.red}
            borderColor={Colors.blue}
            {...register('jobTitle')}
          />
          {
            <p className={cn(captionCn, { 'font-bold': !!errors.jobTitle })}>
              {errors.jobTitle ? `*${errors.jobTitle.message}` : 'Optional'}
            </p>
          }
        </div>

        <div className="py-4 xl:flex">
          <ButtonMighty
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            color="#4400FF"
            text="Update profile"
            borderColor="#ff1dfc"
            margin="0"
            textColor="#fff"
            onClick={() => {
              try {
                handleSubmit(onSubmit)();
              } catch (err: any) {
                console.log(err);
              }
            }}
          />
        </div>
      </form>
    </>
  );
  return (
    <div className="px-4 account-edit-background pt-14">
      <div className="container mx-auto">
        <div className="mb-8 text-white">
          <h1 className="mb-2 text-4xl">Manage account</h1>
          <p className="text-lg">Update your profile and details</p>
        </div>
        <div className="flex flex-col gap-4 mx-auto lg:container lg:flex-row">
          <div className="w-full lg:min-h-screen lg:sticky lg:top-0 lg:w-96">
            {leftContent}
          </div>
          <div className="flex flex-1">
            <div className="flex-1 pb-24">{content}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAccountPage;
